import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { createNewUser, checkINN, getDepartments } from 'service/AdminService'
import { postRegisterUser } from 'store/slices/AdminSlice'
import Notification from 'utils/Notifications'
import { ScaleLoader } from 'react-spinners'
import DepartmentsPanel from './Blocks/DepartmentsPanel'
import styles from './KomurSvyazPage.module.scss'
import {
  getNPAList, createNPA, deleteNPA, updateNPA, downloadNPA,
  getDocumentCategories,
} from 'service/KomurConnectionService'

// ─────────────────────────────────────────────
// Inline registration form (no nested Layout)
// ─────────────────────────────────────────────
function RegisterTab() {
  const dispatch = useDispatch()
  const [loader, setLoader] = useState(false)
  const [first_name, setFirst_name] = useState('')
  const [last_name, setLast_name] = useState('')
  const [middle_name, setMiddle_name] = useState('')
  const [number_phone, setNumber_phone] = useState('')
  const [inn, setInn] = useState('')
  const [position, setPosition] = useState('')
  const [department_id, setDepartment_id] = useState('')
  const [departments, setDepartments] = useState([])

  const [checkInnVal, setCheckInnVal] = useState('')
  const [innCheckResult, setInnCheckResult] = useState(null)
  const [innCheckLoading, setInnCheckLoading] = useState(false)

  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  useEffect(() => {
    getDepartments().then(r => setDepartments(r.data)).catch(() => {})
  }, [])

  const validatePhone = (v) => v.length >= 10 && /^\d+$/.test(v)
  const validateINN = (v) => v && v.toString().length >= 10 && /^\d+$/.test(v)

  const validateField = (name, value) => {
    if (name === 'first_name' || name === 'last_name') {
      return !value || value.trim() === '' ? 'Обязательное поле' : ''
    }
    if (name === 'number_phone') {
      if (!value) return 'Обязательное поле'
      if (!validatePhone(value)) return 'Минимум 10 цифр'
    }
    if (name === 'inn') {
      if (!value) return 'Обязательное поле'
      if (!validateINN(value)) return 'Минимум 10 цифр'
    }
    return ''
  }

  const handleBlur = (name, value) => {
    setTouched(t => ({ ...t, [name]: true }))
    setErrors(e => ({ ...e, [name]: validateField(name, value) }))
  }

  const handleCheckINN = async () => {
    if (!checkInnVal || checkInnVal.length < 10) {
      setNotify({ isOpen: true, message: 'Введите корректный ИНН (минимум 10 цифр)', type: 'warning', sound: 'warning' })
      return
    }
    try {
      setInnCheckLoading(true)
      const response = await checkINN(checkInnVal)
      setInnCheckResult(response.data)
    } catch {
      setNotify({ isOpen: true, message: 'Ошибка при проверке ИНН', type: 'error', sound: 'error' })
    } finally {
      setInnCheckLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const newErrors = {
      first_name: validateField('first_name', first_name),
      last_name: validateField('last_name', last_name),
      number_phone: validateField('number_phone', number_phone),
      inn: validateField('inn', inn),
    }
    setErrors(newErrors)
    setTouched({ first_name: true, last_name: true, number_phone: true, inn: true })
    if (Object.values(newErrors).some(err => err !== '')) {
      setNotify({ isOpen: true, message: 'Пожалуйста, исправьте ошибки в форме', type: 'warning', sound: 'warning' })
      return
    }
    try {
      setLoader(true)
      await createNewUser(first_name, last_name, middle_name, number_phone, inn, inn, true, position, department_id || null)
      dispatch(postRegisterUser({ first_name, last_name, middle_name, number_phone, inn }))
      setNotify({ isOpen: true, message: 'Сотрудник успешно зарегистрирован!', type: 'success', sound: 'success' })
      setFirst_name(''); setLast_name(''); setMiddle_name('')
      setNumber_phone(''); setInn(''); setPosition('')
      setDepartment_id(''); setErrors({}); setTouched({})
    } catch (error) {
      setNotify({ isOpen: true, message: error.response?.data?.message || 'Ошибка регистрации. Попробуйте снова.', type: 'error', sound: 'error' })
    } finally {
      setLoader(false)
    }
  }

  return (
    <div className={styles.regContainer}>
      <div className={styles.regHeader}>
        <p className={styles.regTitle}>Регистрация сотрудника</p>
        <p className={styles.regSubtitle}>Заполните все поля для создания нового сотрудника</p>
      </div>

      {/* INN Check */}
      <div className={styles.innCheckSection}>
        <label className={styles.sectionLabel}>Проверка ИНН в базе данных</label>
        <div className={styles.innCheckWrapper}>
          <input
            type="text"
            className={styles.input}
            style={{ flex: '1 1 260px' }}
            placeholder="Введите ИНН для проверки"
            value={checkInnVal}
            onChange={e => { setCheckInnVal(e.target.value.replace(/\D/g, '')); setInnCheckResult(null) }}
            onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); handleCheckINN() } }}
            maxLength={14}
          />
          <button type="button" className={styles.checkButton} onClick={handleCheckINN} disabled={innCheckLoading}>
            {innCheckLoading ? 'Проверка...' : 'Проверить'}
          </button>
          {innCheckResult && (
            <span className={`${styles.innResult} ${innCheckResult.exists ? styles.innExists : styles.innNotExists}`}>
              {innCheckResult.message}
            </span>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Personal */}
        <div className={styles.section}>
          <label className={styles.sectionLabel}>Личные данные</label>
          <div className={styles.formThreeCol}>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>Фамилия *</label>
              <input type="text" className={`${styles.input} ${touched.last_name && errors.last_name ? styles.inputError : ''}`}
                placeholder="Иванов" value={last_name}
                onChange={e => setLast_name(e.target.value)} onBlur={() => handleBlur('last_name', last_name)} />
              {touched.last_name && errors.last_name && <span className={styles.errorText}>{errors.last_name}</span>}
            </div>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>Имя *</label>
              <input type="text" className={`${styles.input} ${touched.first_name && errors.first_name ? styles.inputError : ''}`}
                placeholder="Иван" value={first_name}
                onChange={e => setFirst_name(e.target.value)} onBlur={() => handleBlur('first_name', first_name)} />
              {touched.first_name && errors.first_name && <span className={styles.errorText}>{errors.first_name}</span>}
            </div>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>Отчество</label>
              <input type="text" className={styles.input} placeholder="Иванович"
                value={middle_name} onChange={e => setMiddle_name(e.target.value)} />
            </div>
          </div>
        </div>

        {/* Contact + Position + Department */}
        <div className={styles.section}>
          <label className={styles.sectionLabel}>Контакты и должность</label>
          <div className={styles.formThreeCol}>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>Номер телефона *</label>
              <input type="tel" className={`${styles.input} ${touched.number_phone && errors.number_phone ? styles.inputError : ''}`}
                placeholder="+996 XXX XXX XXX" value={number_phone}
                onChange={e => setNumber_phone(e.target.value.replace(/\D/g, ''))}
                onBlur={() => handleBlur('number_phone', number_phone)} />
              {touched.number_phone && errors.number_phone && <span className={styles.errorText}>{errors.number_phone}</span>}
            </div>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>Должность</label>
              <input type="text" className={styles.input} placeholder="Например: Инженер..."
                value={position} onChange={e => setPosition(e.target.value)} />
            </div>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>Отдел</label>
              <select className={styles.input} value={department_id} onChange={e => setDepartment_id(e.target.value)}>
                <option value="">— Без отдела —</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* INN */}
        <div className={styles.section}>
          <label className={styles.sectionLabel}>Налоговая информация</label>
          <div className={styles.formTwoCol}>
            <div className={styles.inputWrapper}>
              <label className={styles.inputLabel}>ИНН *</label>
              <input type="text" className={`${styles.input} ${touched.inn && errors.inn ? styles.inputError : ''}`}
                placeholder="Введите ИНН" value={inn}
                onChange={e => setInn(e.target.value.replace(/\D/g, ''))}
                onBlur={() => handleBlur('inn', inn)} maxLength={14} />
              {touched.inn && errors.inn && <span className={styles.errorText}>{errors.inn}</span>}
              <span className={styles.helperText}>ИНН будет использован как пароль</span>
            </div>
          </div>
        </div>

        <div className={styles.submitSection}>
          {loader ? (
            <div className={styles.loaderWrapper}>
              <ScaleLoader color="#736ced" height={35} width={4} />
              <span className={styles.loaderText}>Регистрация...</span>
            </div>
          ) : (
            <button type="submit" className={styles.submitButton}>
              <span className={styles.submitIcon}>✓</span>
              Зарегистрировать сотрудника
            </button>
          )}
        </div>
      </form>
      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

// ─────────────────────────────────────────────
// Helpers for DocsAdminTab
// ─────────────────────────────────────────────
function admFmtDate(str) {
  if (!str) return '—'
  const d = new Date(str)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
function admFmtSize(bytes) {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} КБ`
  return `${(bytes / 1048576).toFixed(1)} МБ`
}
function admFileIcon(ext) {
  const e = (ext || '').toLowerCase()
  if (e === 'pdf') return '📕'
  if (['doc', 'docx'].includes(e)) return '📝'
  if (['xls', 'xlsx'].includes(e)) return '📊'
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(e)) return '🖼️'
  return '📄'
}

// ─────────────────────────────────────────────
// Docs / НПА Admin Tab
// ─────────────────────────────────────────────
function DocsAdminTab() {
  const [npaList, setNpaList]     = useState(null)
  const [categories, setCategories] = useState([])
  const [uploadPending, setUploadPending] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editTitle, setEditTitle] = useState('')
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })
  const fileInputRef = useRef()

  useEffect(() => {
    getNPAList().then(r => setNpaList(r.data)).catch(() => setNpaList([]))
    getDocumentCategories().then(r => setCategories(r.data)).catch(() => {})
  }, [])

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadPending({ file, title: file.name.replace(/\.[^/.]+$/, ''), desc: '', categoryId: '' })
    e.target.value = ''
  }

  const handleUploadConfirm = async () => {
    if (!uploadPending?.title.trim()) return
    const { file, title, desc, categoryId } = uploadPending
    const fd = new FormData()
    fd.append('file', file)
    fd.append('title', title.trim())
    if (desc.trim()) fd.append('description', desc.trim())
    if (categoryId) fd.append('category', categoryId)
    fd.append('is_active', 'true')
    setUploading(true)
    try {
      const r = await createNPA(fd)
      setNpaList(prev => [r.data, ...(prev || [])])
      setUploadPending(null)
      setNotify({ isOpen: true, message: 'НПА успешно загружен', type: 'success' })
    } catch {
      setNotify({ isOpen: true, message: 'Ошибка при загрузке файла', type: 'error' })
    }
    setUploading(false)
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить НПА? Действие необратимо.')) return
    try {
      await deleteNPA(id)
      setNpaList(prev => prev.filter(d => d.id !== id))
    } catch {
      setNotify({ isOpen: true, message: 'Ошибка при удалении', type: 'error' })
    }
  }

  const handleEditSave = async (id) => {
    if (!editTitle.trim()) return
    try {
      const r = await updateNPA(id, { title: editTitle.trim() })
      setNpaList(prev => prev.map(d => d.id === id ? { ...d, title: r.data.title } : d))
      setEditingId(null)
    } catch {
      setNotify({ isOpen: true, message: 'Ошибка при сохранении', type: 'error' })
    }
  }

  const handleDownload = (id, fileName) => {
    downloadNPA(id).then(r => {
      const a = document.createElement('a')
      a.href = r.data.file_url
      a.download = fileName || 'document'
      a.click()
    }).catch(() => {})
  }

  const totalDl    = npaList?.reduce((s, d) => s + (d.download_count || 0), 0) ?? 0
  const totalViews = npaList?.reduce((s, d) => s + (d.view_count    || 0), 0) ?? 0

  return (
    <div className={styles.docsAdmin}>

      {/* ── Header ── */}
      <div className={styles.docsAdminHeader}>
        <div className={styles.docsAdminTitleGroup}>
          <p className={styles.docsAdminTitle}>Управление НПА</p>
          <p className={styles.docsAdminSub}>Нормативно-правовые акты — видны всем сотрудникам</p>
        </div>
        {!uploadPending && (
          <button
            className={styles.docsUploadBtn}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <span>+</span> Загрузить НПА
          </button>
        )}
        <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileSelect} />
      </div>

      {/* ── Stats row ── */}
      <div className={styles.docsStats}>
        <div className={styles.docsStat}>
          <span className={styles.docsStatNum}>{npaList?.length ?? '…'}</span>
          <span className={styles.docsStatLabel}>документов</span>
        </div>
        <div className={styles.docsStat}>
          <span className={styles.docsStatNum}>{totalDl}</span>
          <span className={styles.docsStatLabel}>скачиваний</span>
        </div>
        <div className={styles.docsStat}>
          <span className={styles.docsStatNum}>{totalViews}</span>
          <span className={styles.docsStatLabel}>просмотров</span>
        </div>
      </div>

      {/* ── Upload form ── */}
      {uploadPending && (
        <div className={styles.docsUploadForm}>
          <div className={styles.docsUploadTop}>
            <span className={styles.docsUploadIcon}>{admFileIcon(uploadPending.file.name.split('.').pop())}</span>
            <span className={styles.docsUploadFilename}>{uploadPending.file.name}</span>
            <span className={styles.docsUploadFilesize}>{admFmtSize(uploadPending.file.size)}</span>
          </div>
          <div className={styles.docsUploadFields}>
            <div className={styles.docsUploadField}>
              <label className={styles.docsUploadLabel}>Название НПА *</label>
              <input
                className={styles.docsUploadInput}
                value={uploadPending.title}
                onChange={e => setUploadPending(p => ({ ...p, title: e.target.value }))}
                placeholder="Название документа"
                autoFocus
              />
            </div>
            <div className={styles.docsUploadField}>
              <label className={styles.docsUploadLabel}>Категория</label>
              <select
                className={styles.docsUploadInput}
                value={uploadPending.categoryId}
                onChange={e => setUploadPending(p => ({ ...p, categoryId: e.target.value }))}
              >
                <option value="">— Без категории —</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                ))}
              </select>
            </div>
            <div className={styles.docsUploadFieldFull}>
              <label className={styles.docsUploadLabel}>
                Описание <span className={styles.docsOptional}>(необязательно)</span>
              </label>
              <input
                className={styles.docsUploadInput}
                value={uploadPending.desc}
                onChange={e => setUploadPending(p => ({ ...p, desc: e.target.value }))}
                placeholder="Краткое описание документа…"
              />
            </div>
          </div>
          <div className={styles.docsUploadActions}>
            <button
              className={styles.docsConfirmBtn}
              onClick={handleUploadConfirm}
              disabled={uploading || !uploadPending.title.trim()}
            >
              {uploading ? '⏳ Загрузка…' : '✓ Загрузить'}
            </button>
            <button
              className={styles.docsCancelBtn}
              onClick={() => setUploadPending(null)}
              disabled={uploading}
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      {!npaList && <p className={styles.docsLoading}>Загрузка документов…</p>}
      {npaList && !npaList.length && (
        <div className={styles.docsEmpty}>
          <span className={styles.docsEmptyIcon}>📂</span>
          <p className={styles.docsEmptyTitle}>Документы не загружены</p>
          <p className={styles.docsEmptySub}>Нажмите «Загрузить НПА» чтобы добавить первый документ</p>
        </div>
      )}
      {npaList && npaList.length > 0 && (
        <div className={styles.docsTableWrap}>
          <table className={styles.docsTable}>
            <thead>
              <tr>
                <th>Документ</th>
                <th>Категория</th>
                <th>Размер</th>
                <th>Дата загрузки</th>
                <th>⬇</th>
                <th>👁</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {npaList.map(doc => (
                <tr key={doc.id} className={styles.docsRow}>
                  <td className={styles.docsFileCell}>
                    <span className={styles.docsFileIcon}>{admFileIcon(doc.file_extension)}</span>
                    {editingId === doc.id ? (
                      <input
                        className={styles.docsInlineInput}
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleEditSave(doc.id)
                          if (e.key === 'Escape') setEditingId(null)
                        }}
                        autoFocus
                      />
                    ) : (
                      <span className={styles.docsFileName}>{doc.title}</span>
                    )}
                    {doc.file_extension && (
                      <span className={styles.docsExtBadge}>{doc.file_extension.toUpperCase()}</span>
                    )}
                  </td>
                  <td>
                    {doc.category_name
                      ? <span className={styles.docsCatBadge}>{doc.category_name}</span>
                      : <span className={styles.docsNoCat}>—</span>
                    }
                  </td>
                  <td className={styles.docsMeta}>{admFmtSize(doc.file_size)}</td>
                  <td className={styles.docsMeta}>{admFmtDate(doc.created_at)}</td>
                  <td className={styles.docsCountCell}>{doc.download_count ?? 0}</td>
                  <td className={styles.docsCountCell}>{doc.view_count ?? 0}</td>
                  <td className={styles.docsRowActions}>
                    {editingId === doc.id ? (
                      <>
                        <button className={styles.docsSaveBtn} onClick={() => handleEditSave(doc.id)} title="Сохранить">✓</button>
                        <button className={styles.docsCancelIconBtn} onClick={() => setEditingId(null)} title="Отмена">✕</button>
                      </>
                    ) : (
                      <>
                        <button
                          className={styles.docsEditBtn}
                          onClick={() => { setEditingId(doc.id); setEditTitle(doc.title) }}
                          title="Редактировать название"
                        >✏️</button>
                        <button
                          className={styles.docsDlBtn}
                          onClick={() => handleDownload(doc.id, doc.file_name)}
                          title="Скачать"
                        >⬇</button>
                        <button
                          className={styles.docsDelBtn}
                          onClick={() => handleDelete(doc.id)}
                          title="Удалить"
                        >🗑</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

// ─────────────────────────────────────────────
// Main KomurSvyazPage
// ─────────────────────────────────────────────
const TABS = [
  { key: 'departments', icon: '🏢', label: 'Отделы' },
  { key: 'register',    icon: '👤', label: 'Регистрация сотрудников' },
  { key: 'docs',        icon: '📚', label: 'Документы / НПА' },
]

const KomurSvyazPage = () => {
  const [activeTab, setActiveTab] = useState('departments')

  return (
    <div className={styles.page}>
        <div className={styles.tabs}>
          {TABS.map(({ key, icon, label }) => (
            <button
              key={key}
              className={`${styles.tab} ${activeTab === key ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(key)}
            >
              <span className={styles.tabIcon}>{icon}</span>
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'departments' && <DepartmentsPanel />}
        {activeTab === 'register' && <RegisterTab />}
        {activeTab === 'docs' && <DocsAdminTab />}
      </div>
  )
}

export default KomurSvyazPage
